/* eslint-disable @typescript-eslint/no-unused-vars */

import ClovyrHostingLogo from '@clovyr/bed/assets/images/clovyrhost-icon.svg';
import ClovyrHostingFullLogo from '@clovyr/bed/assets/images/clovyrhost-lockup.svg';

import { WNextBase } from '../api/wnext-base';
import {
  type ComputeProviderConfig,
  type ComputeProviderInfo,
  ComputeProviderName,
  type Instance,
} from '../types';
import { sleep, waitFor } from '../util/sleep';

import {
  type ComputeInstanceImage,
  ComputeInstanceSize,
  type ComputeProvider,
  type CreateInstanceRequest,
  type DestroyInstanceRequest,
  type DestroyInstanceResponse,
  type GetInstanceRequest,
  type InstanceType,
  type InstanceTypeMap,
  type ListInstancesRequest,
  type RebootInstanceRequest,
  type UpdateInstanceRequest,
} from '.';

const sizes: InstanceTypeMap = {
  [ComputeInstanceSize.Micro]: [ComputeInstanceSize.Micro, 4], // $4
  [ComputeInstanceSize.Small]: [ComputeInstanceSize.Small, 6], // $6
  [ComputeInstanceSize.Medium]: [ComputeInstanceSize.Medium, 12], // $12
  [ComputeInstanceSize.Large]: [ComputeInstanceSize.Large, 48], // $48
  [ComputeInstanceSize.XLarge]: [ComputeInstanceSize.XLarge, 48], // $48
  [ComputeInstanceSize.XLargeAI]: [ComputeInstanceSize.XLargeAI, 48], // $48
};

interface WakeupInstanceRequest {
  oldFQDN: string;
  newFQDN: string;
}

export const WNextComputeProviderInfo: ComputeProviderInfo = {
  id: ComputeProviderName.WNext,
  title: 'Clovyr Hosting',
  description: '$PRICE/mo after free trial ends.',
  logoUrl: ClovyrHostingLogo,
  fullLogoUrl: ClovyrHostingFullLogo,
  logoBackgroundColor: '#ffffff',
  logoPad: true,
  defaultRegion: 'us-east',
  regions: [
    { name: 'US East', value: 'us-east', flag: 'USA' },
    { name: 'US West', value: 'us-west', flag: 'USA' },
    { name: 'United Kingdom', value: 'uk', flag: 'UK' },
    { name: 'Canada', value: 'canada', flag: 'Canada' },
    { name: 'Singapore', value: 'singapore', flag: 'Singapore' },
    { name: 'India', value: 'india', flag: 'India' },
    { name: 'Germany', value: 'germany', flag: 'Germany' },
    { name: 'Sydney', value: 'sydney', flag: 'Australia' },
  ],
  instanceTypes: [
    { name: ComputeInstanceSize.Micro, value: ComputeInstanceSize.Micro },
    { name: ComputeInstanceSize.Small, value: ComputeInstanceSize.Small },
    { name: ComputeInstanceSize.Medium, value: ComputeInstanceSize.Medium },
    { name: ComputeInstanceSize.Large, value: ComputeInstanceSize.Large },
    { name: ComputeInstanceSize.XLarge, value: ComputeInstanceSize.XLarge },
    { name: ComputeInstanceSize.XLargeAI, value: ComputeInstanceSize.XLargeAI },
  ],
};

export class WNext extends WNextBase implements ComputeProvider {
  getConfig() {
    const config: ComputeProviderConfig = {
      id: crypto.randomUUID(),
      providerID: ComputeProviderName.WNext,
      name: 'Clovyr Hosting',
    };
    return config;
  }

  setConfig(_config: ComputeProviderConfig) {
    // no-op, no config for this provider
  }

  isConfigured(): boolean {
    return true;
  }

  getInstanceType(size: ComputeInstanceSize): InstanceType {
    return sizes[size];
  }

  getInstanceImage(image: ComputeInstanceImage, region: string): string {
    return image;
  }

  /**
   * Create instance using wNext provider. Waits for an IP to be assigned before
   * returning.
   *
   * @param req
   * @returns
   */
  async createInstance(req: CreateInstanceRequest): Promise<Instance> {
    const res = await this.unwrapRes(this.client.post('/instances', req));
    if (!res?.data) {
      throw new Error('failed to create instance');
    }
    const { instance } = res.data;
    return this.#waitForIP(instance.fqdn, 5000, 120);
  }

  async #waitForIP(fqdn: string, initialSleep: number, maxTries: number): Promise<Instance> {
    return waitFor(initialSleep, maxTries, 1000, async () => {
      const res = await this.getInstance({ id: fqdn });
      const ip = res?.ipv4;
      return [!!ip, res];
    }) as Promise<Instance>;
  }

  async destroyInstance(req: DestroyInstanceRequest): Promise<DestroyInstanceResponse> {
    return this.unwrap(await this.client.delete(`instances/${req.id}`, req));
  }

  async rebootInstance(req: RebootInstanceRequest): Promise<void> {
    const path = `instances/${encodeURIComponent(req.id)}`;
    let command = (await this.unwrap<InstanceCommand>(
      await this.client.post(
        `${path}/reboot`,
        { id: req.requestID ?? crypto.randomUUID() },
        {
          signal: AbortSignal.timeout(15000),
        }
      )
    )) as InstanceCommand;
    const deadline = Date.now() + 2 * 60 * 1000;
    while (command.status === 'queued' || command.status === 'running') {
      if (Date.now() >= deadline) {
        throw new Error('Provider reboot is still pending. Check the host before retrying.');
      }
      // eslint-disable-next-line no-await-in-loop
      await sleep(1000);
      // eslint-disable-next-line no-await-in-loop
      const response = await this.client.get(`${path}/commands/${command.id}`, {
        signal: AbortSignal.timeout(10000),
        cache: 'no-store',
      });
      // eslint-disable-next-line no-await-in-loop
      command = (await this.unwrap<InstanceCommand>(response)) as InstanceCommand;
    }
    if (command.status !== 'succeeded') {
      throw new Error(command.error || 'Provider reboot failed.');
    }
  }

  async updateInstance(req: UpdateInstanceRequest): Promise<Instance> {
    // TODO: impl
    throw new Error('not implemented');
  }

  // List of instances we have access to
  async listInstances(_req: ListInstancesRequest): Promise<Instance[]> {
    const resp = await this.client.get(`instances`);
    if (resp instanceof Error) {
      throw resp;
    }
    if (resp.status !== 200) {
      throw new Error(`unexpected status code from getInstance endpoint: ${resp.status}`);
    }
    return resp.json();
  }

  // Fetch instance details, if authorized
  async getInstance(req: GetInstanceRequest): Promise<Instance | undefined> {
    const resp = await this.client.get(`instances/${req.id}`);
    if (resp instanceof Error) {
      throw resp;
    }
    if (resp.status === 200) {
      return resp.json();
    }
    if (resp.status === 404) {
      return undefined;
    }
    throw new Error(`unexpected status code from getInstance endpoint: ${resp.status}`);
  }

  async wakeup(req: WakeupInstanceRequest): Promise<void> {
    await this.unwrapRes(
      this.client.post(`/instances/${req.oldFQDN}/wakeup`, { newFQDN: req.newFQDN })
    );
  }
}

interface InstanceCommand {
  id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  error?: string;
}
